/** The navigation only appears when it offers a real choice. */
export const STORE_CATEGORY_NAV_MIN = 2;
/**
 * Configured store groups (DIRECT_SALES_FORM_SALE_ITEM_GROUP), stored as an
 * array or a comma-separated string.
 *
 * @function readStoreGroupIds
 * @since 1.0.0
 */
export function readStoreGroupIds(saleItemGroup) {
    const values = Array.isArray(saleItemGroup)
        ? saleItemGroup.map(String)
        : typeof saleItemGroup === 'string'
            ? [saleItemGroup]
            : [];
    return values
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter((value) => !!value);
}
/**
 * Primary and secondary categories of a product, without duplicates.
 *
 * @function saleItemGroups
 * @since 1.0.0
 */
export function saleItemGroups(saleItem) {
    var _a;
    const groups = [saleItem.group, ...((_a = saleItem.groups) !== null && _a !== void 0 ? _a : [])].filter((group) => group != null);
    return groups.filter((group, index) => groups.findIndex((entry) => entry.id === group.id) === index);
}
/**
 * @function saleItemGroupTitle
 * @since 1.0.0
 */
export function saleItemGroupTitle(group, language) {
    var _a, _b, _c, _d;
    return (((_b = (_a = group.translations) === null || _a === void 0 ? void 0 : _a.find((entry) => { var _a; return ((_a = entry.language) === null || _a === void 0 ? void 0 : _a.code) === language; })) === null || _b === void 0 ? void 0 : _b.title) ||
        ((_d = (_c = group.translations) === null || _c === void 0 ? void 0 : _c.find((entry) => !!entry.title)) === null || _d === void 0 ? void 0 : _d.title) ||
        group.title ||
        '');
}
/**
 * Categories of the store's category navigation: the configured store
 * groups, otherwise every category of the products, keeping only the ones
 * that have a product. A `?category=` outside the configured groups is kept
 * so its pill shows as selected. Mirrors `storeCategoryIds` of the backend
 * sitemap.
 *
 * @function buildStoreCategories
 * @since 1.0.0
 */
export function buildStoreCategories(saleItems, saleItemGroup, language, activeCategoryId) {
    const groups = new Map();
    for (const saleItem of saleItems) {
        for (const group of saleItemGroups(saleItem)) {
            if (!groups.has(group.id)) {
                groups.set(group.id, group);
            }
        }
    }
    const configured = readStoreGroupIds(saleItemGroup);
    const ids = configured.length > 0 ? configured : Array.from(groups.keys());
    if (activeCategoryId && !ids.includes(activeCategoryId)) {
        ids.push(activeCategoryId);
    }
    return Array.from(new Set(ids))
        .map((id) => groups.get(id))
        .filter((group) => group != null)
        .map((group) => {
        var _a;
        return ({
            id: group.id,
            title: saleItemGroupTitle(group, language),
            order: (_a = group.order) !== null && _a !== void 0 ? _a : 0
        });
    })
        .filter((category) => !!category.title)
        .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, language));
}
/**
 * Title of the product's primary category, for analytics.
 *
 * @function saleItemCategoryTitle
 * @since 1.0.0
 */
export function saleItemCategoryTitle(saleItem, language) {
    const group = saleItem ? saleItemGroups(saleItem)[0] : null;
    return group ? saleItemGroupTitle(group, language) || undefined : undefined;
}
/**
 * Title of a category found among the products' categories, or null.
 *
 * @function findStoreCategoryTitle
 * @since 1.0.0
 */
export function findStoreCategoryTitle(saleItems, categoryId, language) {
    if (!categoryId) {
        return null;
    }
    for (const saleItem of saleItems) {
        const group = saleItemGroups(saleItem).find((entry) => entry.id === categoryId);
        if (group) {
            return saleItemGroupTitle(group, language) || null;
        }
    }
    return null;
}
/**
 * Query string of the store filtered on a category (null: all products).
 * The ?product= preselection was consumed on load, so it goes.
 *
 * @function storeCategorySearch
 * @since 1.0.0
 */
export function storeCategorySearch(search, categoryId) {
    const params = new URLSearchParams(search);
    params.delete('product');
    if (categoryId) {
        params.set('category', categoryId);
    }
    else {
        params.delete('category');
    }
    const value = params.toString();
    return value ? `?${value}` : '';
}
